import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ListChecksIcon } from 'lucide-react';
import {
  Button,
  ConfirmDialog,
  DataTable,
  DatePicker,
  Drawer,
  EmptyState,
  ErrorNotice,
  Field,
  PageHeading,
  Pagination,
  Panel,
  Pill,
  SearchInput,
  SegmentedControl,
  SelectInput,
  SkeletonTable,
  TextArea,
  TextInput,
  type DataTableHeader,
  type DataTableSort,
  type PillTone,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { PERMISSION_VIEW_DEV_TRACKER, hasPermission, readMyIdentity } from '@/lib/events';
import { sortRows } from '@/lib/facet-table';
import { useTableSort } from '@/lib/use-table-sort';
import { useUrlPage } from '@/lib/use-list-query-state';
import {
  DEV_AREA,
  DEV_AREA_OPTIONS,
  DEV_STAGE,
  DEV_STAGE_OPTIONS,
  areaLabel,
  createDevFeature,
  listDevFeatures,
  readableDay,
  removeDevFeature,
  setDevFeatureStage,
  stageLabel,
  todayIso,
  updateDevFeature,
  type DevArea,
  type DevFeature,
  type DevStage,
} from '@/lib/dev-tracker';

const PAGE_SIZE = 25;
const QUERY_KEY = ['dev-features'] as const;

const STAGE_TONES: Readonly<Record<DevStage, PillTone>> = {
  [DEV_STAGE.notStarted]: 'neutral',
  [DEV_STAGE.inDevelopment]: 'info',
  [DEV_STAGE.bceReview]: 'warning',
  [DEV_STAGE.revisions]: 'warning',
  [DEV_STAGE.approved]: 'brand',
  [DEV_STAGE.live]: 'success',
};

/** The filter above the table: everything, what is moving, what shipped, what has not begun. */
const VIEW = { all: 'all', inProgress: 'in_progress', live: 'live', notStarted: 'not_started' } as const;
type View = (typeof VIEW)[keyof typeof VIEW];
const IN_PROGRESS: readonly DevStage[] = [DEV_STAGE.inDevelopment, DEV_STAGE.bceReview, DEV_STAGE.revisions, DEV_STAGE.approved];

function inView(feature: DevFeature, view: View): boolean {
  if (view === VIEW.inProgress) return IN_PROGRESS.includes(feature.stage);
  if (view === VIEW.live) return feature.stage === DEV_STAGE.live;
  if (view === VIEW.notStarted) return feature.stage === DEV_STAGE.notStarted;
  return true;
}

const SORT_FIELDS = { feature: 'title', stage: 'stageOrder', since: 'stageSince', live: 'liveOn' } as const;
const SORT_KEYS = Object.values(SORT_FIELDS);
const HEADERS: readonly DataTableHeader[] = [
  { label: 'Feature', sortKey: SORT_FIELDS.feature },
  { label: 'Stage', sortKey: SORT_FIELDS.stage },
  { label: 'In this stage since', sortKey: SORT_FIELDS.since },
  { label: 'Went live', sortKey: SORT_FIELDS.live },
];
/** Most recently moved first, so the latest progress is at the top. */
const DEFAULT_SORT: DataTableSort = { key: SORT_FIELDS.since, direction: 'desc' };

/**
 * The development tracker (Roy's portal list, item 1; 2026-09-27): every
 * feature BCE asked for, the stage it is in, and the day it reached each
 * stage. Past days are allowed, so work that shipped before the tracker can
 * be marked Live on the day it shipped.
 *
 * @returns The page.
 */
export function DevTrackerPage(): React.ReactElement {
  const [search, setSearch] = useState('');
  const [view, setView] = useState<View>(VIEW.all);
  const [openId, setOpenId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const { sort, onSort } = useTableSort(SORT_KEYS, DEFAULT_SORT);
  const { page, setPage } = useUrlPage();

  const identity = useQuery({ queryKey: ['my-identity'], queryFn: ({ signal }) => readMyIdentity(signal), staleTime: 5 * 60_000 });
  // The grant that opens the page also records moves: the tracker is for a few named people.
  const canEdit = hasPermission(identity.data, PERMISSION_VIEW_DEV_TRACKER);
  const features = useQuery({ queryKey: QUERY_KEY, queryFn: ({ signal }) => listDevFeatures(signal) });

  const all = features.data ?? [];
  const counts: Readonly<Record<View, number>> = {
    [VIEW.all]: all.length,
    [VIEW.inProgress]: all.filter((feature) => inView(feature, VIEW.inProgress)).length,
    [VIEW.live]: all.filter((feature) => inView(feature, VIEW.live)).length,
    [VIEW.notStarted]: all.filter((feature) => inView(feature, VIEW.notStarted)).length,
  };
  const needle = search.trim().toLowerCase();
  const rows = sortRows(
    all
      .filter((feature) => inView(feature, view))
      .filter((feature) => needle === '' || `${feature.title} ${feature.summary} ${areaLabel(feature.area)}`.toLowerCase().includes(needle))
      .map((feature) => ({
        ...feature,
        stageOrder: DEV_STAGE_OPTIONS.findIndex((option) => option.key === feature.stage),
        stageSince: feature.stageSince ?? '',
        liveOn: feature.liveOn ?? '',
      })),
    sort ?? DEFAULT_SORT,
  );
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const shown = rows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const open = all.find((feature) => feature.id === openId) ?? null;

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="HELP"
        title="Development tracker"
        subtitle="Every feature BCE asked for, where it stands, and the day it reached each stage."
        {...(canEdit ? { action: <Button onClick={() => setAdding(true)}>Add feature</Button> } : {})}
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SearchInput
          label="Search features"
          placeholder="A feature, a word from its summary, an area"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
          className="w-full max-w-sm"
        />
        <SegmentedControl
          label="Which features to show"
          options={[
            { key: VIEW.all, label: `All (${counts[VIEW.all]})` },
            { key: VIEW.inProgress, label: `In progress (${counts[VIEW.inProgress]})` },
            { key: VIEW.live, label: `Live (${counts[VIEW.live]})` },
            { key: VIEW.notStarted, label: `Not started (${counts[VIEW.notStarted]})` },
          ]}
          value={view}
          onChange={(next) => {
            setView(next);
            setPage(1);
          }}
        />
      </div>

      {features.isError ? (
        <ErrorNotice title="Couldn’t load the tracker" message={errorMessage(features.error)} />
      ) : features.isPending ? (
        <SkeletonTable rows={6} columns={['w-[45%]', 'w-[15%]', 'w-[15%]', 'w-[15%]']} label="Loading features…" />
      ) : rows.length === 0 ? (
        <Panel>
          <EmptyState
            icon={<ListChecksIcon aria-hidden className="size-6" />}
            title={all.length === 0 ? 'No features on the tracker yet' : 'No features match'}
            message={
              all.length === 0
                ? 'Each feature BCE asks for goes here with its stage and the day it got there, so progress is visible without chasing email.'
                : 'Try a different search or show all features.'
            }
            {...(all.length === 0 && canEdit ? { action: <Button onClick={() => setAdding(true)}>Add feature</Button> } : {})}
          />
        </Panel>
      ) : (
        <>
          <DataTable guide="dev-tracker-table" headers={HEADERS} {...(sort === undefined ? {} : { sort })} onSort={onSort}>
            {shown.map((feature, index) => (
              <tr key={feature.id} className="border-t border-portal-line align-middle">
                <td className="p-3 text-portal-small">
                  <button
                    type="button"
                    data-guide={index === 0 ? 'dev-tracker-open-first' : undefined}
                    onClick={() => setOpenId(feature.id)}
                    className="text-left font-bold text-brand underline-offset-2 hover:underline"
                  >
                    {feature.title}
                  </button>
                  <span className="block text-portal-micro text-portal-muted">{areaLabel(feature.area)}</span>
                </td>
                <td className="p-3 text-portal-small">
                  <Pill tone={STAGE_TONES[feature.stage]}>{stageLabel(feature.stage)}</Pill>
                </td>
                <td className="p-3 text-portal-small whitespace-nowrap text-portal-muted">{readableDay(feature.stageSince || null)}</td>
                <td className="p-3 text-portal-small whitespace-nowrap text-portal-muted">{readableDay(feature.liveOn || null)}</td>
              </tr>
            ))}
          </DataTable>
          {totalPages > 1 ? (
            <div className="mt-4">
              <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
            </div>
          ) : null}
        </>
      )}

      <FeatureDrawer feature={open} canEdit={canEdit} onClose={() => setOpenId(null)} />
      <AddFeatureDrawer open={adding} onClose={() => setAdding(false)} onAdded={(id) => { setAdding(false); setOpenId(id); }} />
    </section>
  );
}

/** The stage after this one; Live stays Live. */
function nextStage(stage: DevStage): DevStage {
  const index = DEV_STAGE_OPTIONS.findIndex((option) => option.key === stage);
  return DEV_STAGE_OPTIONS[Math.min(index + 1, DEV_STAGE_OPTIONS.length - 1)]?.key ?? stage;
}

/** Links typed one per line, blanks dropped. */
function linksFrom(text: string): string[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

/**
 * The fields a feature is described by, shared by adding and editing.
 *
 * @param props - The values and their setters.
 * @returns The fields.
 */
function DetailFields({
  idPrefix,
  title,
  setTitle,
  area,
  setArea,
  summary,
  setSummary,
  links,
  setLinks,
}: {
  readonly idPrefix: string;
  readonly title: string;
  readonly setTitle: (value: string) => void;
  readonly area: DevArea;
  readonly setArea: (value: DevArea) => void;
  readonly summary: string;
  readonly setSummary: (value: string) => void;
  readonly links: string;
  readonly setLinks: (value: string) => void;
}): React.ReactElement {
  return (
    <>
      <Field label="Feature" htmlFor={`${idPrefix}-title`} required>
        <TextInput id={`${idPrefix}-title`} maxLength={120} value={title} onChange={(event) => setTitle(event.target.value)} />
      </Field>
      <Field label="Area" htmlFor={`${idPrefix}-area`}>
        <SelectInput
          id={`${idPrefix}-area`}
          value={area}
          onChange={(event) => setArea(DEV_AREA_OPTIONS.find((option) => option.key === event.target.value)?.key ?? DEV_AREA.other)}
        >
          {DEV_AREA_OPTIONS.map((option) => (
            <option key={option.key} value={option.key}>
              {option.label}
            </option>
          ))}
        </SelectInput>
      </Field>
      <Field label="What it does" htmlFor={`${idPrefix}-summary`}>
        <TextArea id={`${idPrefix}-summary`} rows={4} maxLength={2000} value={summary} onChange={(event) => setSummary(event.target.value)} />
      </Field>
      <Field label="Links" htmlFor={`${idPrefix}-links`} hint="Pull requests, docs or screenshots, one per line.">
        <TextArea id={`${idPrefix}-links`} rows={3} value={links} onChange={(event) => setLinks(event.target.value)} />
      </Field>
    </>
  );
}

/**
 * A stage and the day it was reached, the pair every move records.
 *
 * @param props - The values and their setters.
 * @returns The fields.
 */
function StageFields({
  idPrefix,
  stage,
  setStage,
  on,
  setOn,
}: {
  readonly idPrefix: string;
  readonly stage: DevStage;
  readonly setStage: (value: DevStage) => void;
  readonly on: string;
  readonly setOn: (value: string) => void;
}): React.ReactElement {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <Field guide="dev-stage" label="Stage" htmlFor={`${idPrefix}-stage`}>
        <SelectInput
          id={`${idPrefix}-stage`}
          value={stage}
          onChange={(event) => setStage(DEV_STAGE_OPTIONS.find((option) => option.key === event.target.value)?.key ?? DEV_STAGE.notStarted)}
        >
          {DEV_STAGE_OPTIONS.map((option) => (
            <option key={option.key} value={option.key}>
              {option.label}
            </option>
          ))}
        </SelectInput>
      </Field>
      <Field guide="dev-stage-on" label="As of" htmlFor={`${idPrefix}-on`} hint="Today or an earlier day.">
        <DatePicker id={`${idPrefix}-on`} value={on} max={todayIso()} onChange={setOn} />
      </Field>
    </div>
  );
}

/**
 * Adding a feature. It can start at any stage as of any past day, which is how
 * shipped work goes in as Live on the day it shipped.
 *
 * @param props - Whether it is open, and what to do when done.
 * @returns The side panel.
 */
function AddFeatureDrawer({
  open,
  onClose,
  onAdded,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onAdded: (id: string) => void;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [area, setArea] = useState<DevArea>(DEV_AREA.clientPortal);
  const [summary, setSummary] = useState('');
  const [links, setLinks] = useState('');
  const [stage, setStage] = useState<DevStage>(DEV_STAGE.notStarted);
  const [on, setOn] = useState(todayIso());

  const add = useMutation({
    mutationFn: () => createDevFeature({ title: title.trim(), area, summary: summary.trim(), links: linksFrom(links), stage, on }),
    onSuccess: async (feature) => {
      setTitle('');
      setSummary('');
      setLinks('');
      setStage(DEV_STAGE.notStarted);
      setOn(todayIso());
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      onAdded(feature.id);
    },
  });

  return (
    <Drawer
      open={open}
      title="Add a feature"
      subtitle="Record work that shipped earlier as Live on the day it shipped."
      onClose={() => {
        if (!add.isPending) onClose();
      }}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={title.trim().length === 0 || on === '' || add.isPending} onClick={() => add.mutate()}>
            {add.isPending ? 'Adding…' : 'Add feature'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <DetailFields
          idPrefix="new-feature"
          title={title}
          setTitle={setTitle}
          area={area}
          setArea={setArea}
          summary={summary}
          setSummary={setSummary}
          links={links}
          setLinks={setLinks}
        />
        <StageFields idPrefix="new-feature" stage={stage} setStage={setStage} on={on} setOn={setOn} />
        {add.isError ? <ErrorNotice inline title="Feature not added" message={errorMessage(add.error)} /> : null}
      </div>
    </Drawer>
  );
}

/**
 * One feature: its details, the stage history with dates, and (for admins)
 * editing, recording a move and taking it off the tracker.
 *
 * @param props - The feature (null when closed) and whether the viewer may edit.
 * @returns The side panel.
 */
function FeatureDrawer({
  feature,
  canEdit,
  onClose,
}: {
  readonly feature: DevFeature | null;
  readonly canEdit: boolean;
  readonly onClose: () => void;
}): React.ReactElement {
  return (
    <Drawer
      open={feature !== null}
      title={feature?.title ?? 'Feature'}
      {...(feature ? { subtitle: `${areaLabel(feature.area)} · ${stageLabel(feature.stage)}` } : {})}
      onClose={onClose}
    >
      {/* Keyed so the form starts from the feature it is showing. */}
      {feature === null ? null : <FeatureBody key={feature.id} feature={feature} canEdit={canEdit} onRemoved={onClose} />}
    </Drawer>
  );
}

function FeatureBody({
  feature,
  canEdit,
  onRemoved,
}: {
  readonly feature: DevFeature;
  readonly canEdit: boolean;
  readonly onRemoved: () => void;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY });

  const [title, setTitle] = useState(feature.title);
  const [area, setArea] = useState<DevArea>(feature.area);
  const [summary, setSummary] = useState(feature.summary);
  const [links, setLinks] = useState(feature.links.join('\n'));
  // The usual move is to the next stage, so that is what the form offers first.
  const [stage, setStage] = useState<DevStage>(nextStage(feature.stage));
  const [on, setOn] = useState(todayIso());
  const [note, setNote] = useState('');
  const [confirmRemove, setConfirmRemove] = useState(false);

  const save = useMutation({
    mutationFn: () => updateDevFeature(feature.id, { title: title.trim(), area, summary: summary.trim(), links: linksFrom(links) }),
    onSuccess: refresh,
  });
  const move = useMutation({
    mutationFn: () => setDevFeatureStage(feature.id, { stage, on, ...(note.trim() ? { note: note.trim() } : {}) }),
    onSuccess: async () => {
      setNote('');
      await refresh();
    },
  });
  const remove = useMutation({
    mutationFn: () => removeDevFeature(feature.id),
    onSuccess: async () => {
      await refresh();
      onRemoved();
    },
  });

  const detailsChanged =
    title.trim() !== feature.title || area !== feature.area || summary.trim() !== feature.summary || linksFrom(links).join('\n') !== feature.links.join('\n');

  return (
    <div className="space-y-6">
      {canEdit ? (
        <section className="space-y-4">
          <DetailFields
            idPrefix="feature"
            title={title}
            setTitle={setTitle}
            area={area}
            setArea={setArea}
            summary={summary}
            setSummary={setSummary}
            links={links}
            setLinks={setLinks}
          />
          <div className="flex justify-end">
            <Button variant="secondary" disabled={!detailsChanged || title.trim() === '' || save.isPending} onClick={() => save.mutate()}>
              {save.isPending ? 'Saving…' : 'Save details'}
            </Button>
          </div>
          {save.isError ? <ErrorNotice inline title="Details not saved" message={errorMessage(save.error)} /> : null}
        </section>
      ) : (
        <section className="space-y-3">
          {feature.summary ? <p className="text-portal-small leading-[1.6] whitespace-pre-line">{feature.summary}</p> : null}
          {feature.links.length > 0 ? (
            <ul className="space-y-1 text-portal-small">
              {feature.links.map((link) => (
                <li key={link}>
                  <a href={link} target="_blank" rel="noreferrer" className="break-all text-brand-link underline-offset-2 hover:underline">
                    {link}
                  </a>
                </li>
              ))}
            </ul>
          ) : null}
        </section>
      )}

      {canEdit ? (
        <section data-guide="dev-stage-record" className="space-y-4 border-t border-portal-line pt-5">
          <p className="text-portal-small font-bold">Record a stage</p>
          <StageFields idPrefix="feature" stage={stage} setStage={setStage} on={on} setOn={setOn} />
          <Field guide="dev-stage-note" label="Note" htmlFor="feature-note">
            <TextInput id="feature-note" maxLength={500} value={note} onChange={(event) => setNote(event.target.value)} placeholder="What changed, or a PR number" />
          </Field>
          <div className="flex justify-end">
            <Button guide="dev-stage-mark" disabled={on === '' || move.isPending} onClick={() => move.mutate()}>
              {move.isPending ? 'Recording…' : `Mark ${stageLabel(stage)}`}
            </Button>
          </div>
          {move.isError ? <ErrorNotice inline title="Stage not recorded" message={errorMessage(move.error)} /> : null}
        </section>
      ) : null}

      <section data-guide="dev-stage-history" className="border-t border-portal-line pt-5">
        <p className="text-portal-small font-bold">History</p>
        <ol className="mt-2">
          {[...feature.history].reverse().map((event, index) => (
            <li key={`${event.recordedAt}-${index}`} className="border-t border-portal-line py-2.5 text-portal-small first:border-0">
              <div className="flex items-center justify-between gap-3">
                <Pill tone={STAGE_TONES[event.stage]}>{stageLabel(event.stage)}</Pill>
                <span className="whitespace-nowrap text-portal-muted">{readableDay(event.on)}</span>
              </div>
              <p className="mt-1 text-portal-micro text-portal-muted">
                Recorded by {event.recordedBy}
                {event.note ? `: ${event.note}` : ''}
              </p>
            </li>
          ))}
        </ol>
      </section>

      {canEdit ? (
        <div className="border-t border-portal-line pt-5">
          <Button variant="secondary" onClick={() => setConfirmRemove(true)}>
            Remove from tracker
          </Button>
        </div>
      ) : null}

      <ConfirmDialog
        open={confirmRemove}
        title="Remove this feature?"
        message="It comes off the tracker. Its record and history are kept."
        confirmLabel="Remove"
        tone="danger"
        pending={remove.isPending}
        onConfirm={() => remove.mutate()}
        onCancel={() => setConfirmRemove(false)}
      />
    </div>
  );
}
